import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { ownResourceKey } from '../../../ownKey'
import { ownNewSaleStarted, ownSaleCleared, ownSaleParked, ownSaleWindowClosed } from './saleSlice'
import type { OwnPaymentKind } from './saleState'

/**
 * Попытка оплаты собственной кассы (RP-PAY-ONE, шаг 7) — по образцу среза попыток AQSI: одно нажатие «Картой» / «Наличными» /
 * «Повторить оплату» — одно окно до итога. Здесь только нажатия кассира («Свернуть», «Закрыть») и то, что команду оплаты не отправили
 * (касса не готова в момент нажатия); ход и итог говорит агент (`OwnSaleState.verdict`, журнал агента). Срок жизни `screen`, как у
 * продажи окна: после F5 продажи в окне нет, неразобранную запись показывает журнал агента, и окно подхватывает её своим ключом.
 */
export interface OwnPaymentAttempt {
  /** UUID нажатия; подхваченная попытка (запись журнала агента после F5) — `journal:<ключ операции>`. Ключ монтирования окна. */
  id: string
  kind: OwnPaymentKind
  /** Когда нажали (ISO): счётчик «идёт m:ss» этапа «Уточняем». */
  startedAt: string
  /** Команду оплаты не отправили (порог агента закрылся в момент нажатия): этап «Оплата не началась», а не строка внизу. */
  aborted: boolean
  /** «Свернуть»: окно не показывается, в блоке состояния — «Ход оплаты: …»; само не разворачивается. */
  collapsed: boolean
  /** «Закрыть» окна «Оплата не началась»: до следующего нажатия окна нет. */
  closed: boolean
}

export interface OwnPaymentAttemptSliceState {
  byResource: Record<string, OwnPaymentAttempt>
}

const initialState: OwnPaymentAttemptSliceState = { byResource: {} }

interface AttemptRef {
  resourceId: string
  /** Ключ окна, которое видит кассир: попытка другого ключа подхватывается заново. */
  id: string
  kind: OwnPaymentKind
}

/** Попытка этого окна; другой ключ (запись журнала после F5) — подхватывается тем ключом, каким её показывает окно. */
function attemptOf(state: OwnPaymentAttemptSliceState, ref: AttemptRef): OwnPaymentAttempt {
  const key = ownResourceKey(ref.resourceId)
  const current = state.byResource[key]
  if (current !== undefined && current.id === ref.id) return current
  const adopted: OwnPaymentAttempt = { id: ref.id, kind: ref.kind, startedAt: new Date().toISOString(), aborted: false, collapsed: false, closed: false }
  state.byResource[key] = adopted
  return adopted
}

const drop = (state: OwnPaymentAttemptSliceState, resourceId: string): void => {
  delete state.byResource[ownResourceKey(resourceId)]
}

const slice = createSlice({
  name: 'own/paymentAttempt',
  initialState,
  reducers: {
    /** Нажата оплата: новая попытка — новое окно; прежние «свёрнуто» и «закрыто» не наследуются. */
    ownPaymentAttemptStarted: {
      reducer(state, { payload }: PayloadAction<{ resourceId: string; id: string; kind: OwnPaymentKind; startedAt: string }>) {
        const { resourceId, id, kind, startedAt } = payload
        state.byResource[ownResourceKey(resourceId)] = { id, kind, startedAt, aborted: false, collapsed: false, closed: false }
      },
      prepare(ref: { resourceId: string; kind: OwnPaymentKind }) {
        return { payload: { ...ref, id: globalThis.crypto.randomUUID(), startedAt: new Date().toISOString() } }
      },
    },
    /** Агент команду не принял до кассы (порог закрылся): «Оплата не началась» в окне этой попытки. */
    ownPaymentAborted(state, { payload }: PayloadAction<{ resourceId: string }>) {
      const attempt = state.byResource[ownResourceKey(payload.resourceId)]
      if (attempt !== undefined) attempt.aborted = true
    },
    ownPaymentCollapsed(state, { payload }: PayloadAction<AttemptRef>) {
      attemptOf(state, payload).collapsed = true
    },
    /** Развернуть: «Ход оплаты: …» — кассир сам открыл окно. */
    ownPaymentExpanded(state, { payload }: PayloadAction<AttemptRef>) {
      const attempt = attemptOf(state, payload)
      attempt.collapsed = false
      attempt.closed = false
    },
    ownPaymentClosed(state, { payload }: PayloadAction<{ resourceId: string }>) {
      const attempt = state.byResource[ownResourceKey(payload.resourceId)]
      if (attempt === undefined) return
      attempt.closed = true
      attempt.collapsed = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => drop(state, action.payload.resourceId))
    // Новая продажа, очищенная корзина, отложенная продажа и закрытое окно — новой попытке нечего наследовать.
    builder.addCase(ownNewSaleStarted, (state, action) => drop(state, action.payload.resourceId))
    builder.addCase(ownSaleCleared, (state, action) => drop(state, action.payload.resourceId))
    builder.addCase(ownSaleParked, (state, action) => drop(state, action.payload.resourceId))
    builder.addCase(ownSaleWindowClosed, (state, action) => drop(state, action.payload.resourceId))
  },
})

export const { ownPaymentAttemptStarted, ownPaymentAborted, ownPaymentCollapsed, ownPaymentExpanded, ownPaymentClosed } = slice.actions
export const ownPaymentAttemptSliceReducer = slice.reducer

/** Попытка оплаты окна источника; нет — оплаты в этом окне не нажимали. */
export const paymentAttemptOf = (state: OwnPaymentAttemptSliceState, resourceId: string): OwnPaymentAttempt | null =>
  state.byResource[ownResourceKey(resourceId)] ?? null
